import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { storage, db } from "./Firebase";
import {
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  updateDoc,
  onSnapshot,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { v4 as uuidv4 } from "uuid";
import VoucherDocument from "./VoucherDocument";
import { pdf } from "@react-pdf/renderer";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { userName, userEmail } from "./UserData";

const Container = styled.div`
  max-width: 1200px;
  margin: 0px auto 40px;
  padding: 10px;
`;

const FormGroup = styled.div`
  position: relative;
`;

const FormContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  label {
    position: absolute;
    top: -5px;
    left: 10px;
    background-color: white;
    padding: 0 5px;
    font-size: 12px;
    color: #c20000; /* Color del texto del label */
  }
  input {
    width: 280px;
    padding: 8px;
    margin: 5px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    &:focus {
      outline: none;
      border-color: #c20000; /* Color del borde en focus */
    }
  }
  textarea {
    width: 95%;
    margin: 5px;
  }
`;

const BusContainer = styled.div`
  display: flex;
  gap: 20px;
  margin: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Title = styled.h5`
  margin-bottom: 10px;
`;

const Bus = styled.div`
  border: 2px solid #c20000;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  width: 220px;
`;

const Deck = styled.div`
  width: auto;
  border-top: 2px solid #c20000;
  padding: 14px;
  display: flex;
  flex-wrap: wrap;
  margin: 10px;
`;

const Seat = styled.div`
  width: 30px;
  height: 35px;
  border: 1px solid #c20000;
  background-color: ${(props) => props.color || "#f5f5f5"};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #ffe6e6;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  max-height: 90vh; /* Máximo 90% del alto de la ventana */
  max-width: 90vw; /* Máximo 90% del ancho de la ventana */
  overflow: auto; /* Activa el scroll si el contenido se desborda */
  box-sizing: border-box; /* Asegura que el padding no exceda el tamaño total */
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const Button = styled.button`
  background-color: #c20000;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  margin: 10px;
  cursor: pointer;
  &:hover {
    background-color: #a80000;
  }
`;

const BusDiagrama = () => {
  const [buses, setBuses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    dni: "",
    fechaNacimiento: "",
    nacionalidad: "",
    email: "",
    origen: "",
    destino: "",
    fechaSalida: "",
    horaSalida: "",
    fechaRegreso: "",
    horaRegreso: "",
    observaciones: "",
  });
  const [defaultData, setDefaultData] = useState({
    origen: "",
    destino: "",
    fechaSalida: "",
    horaSalida: "",
    fechaRegreso: "",
    horaRegreso: "",
    observaciones: "",
  });
  const [newBus, setNewBus] = useState({ interno: "", semicama: "", cama: "" });
  const [showAddBusModal, setShowAddBusModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [groupName, setGroupName] = useState(""); // Para el nombre del grupo
  const [showGroupNameModal, setShowGroupNameModal] = useState(false);
  const [firmaCorreo, setFirmaCorreo] = useState("");
  const [correoUsuario, setCorreoUsuario] = useState("");
  const [isSeatOccupied, setIsSeatOccupied] = useState(false);

  useEffect(() => {
      setFirmaCorreo(userName); // Actualiza firmaCorreo con userName
      setCorreoUsuario(userEmail);
    }, []);

  // Load data from Firestore

  const fetchData = async () => {
    const querySnapshot = await getDocs(collection(db, "buses"));
    const loadedBuses = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setBuses(loadedBuses);
  };

  /**
   * useEffect para sincronizar en tiempo real la colección "buses" desde Firestore.
   *
   * Este efecto se ejecuta al montar el componente y establece una suscripción
   * en tiempo real a la colección "buses" en Firestore. Cualquier cambio en la
   * colección actualiza automáticamente el estado `buses` con los datos más recientes.
   *
   * @returns {Function} - Limpia la suscripción al desmontar el componente.
   */

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "buses"), (snapshot) => {
      const updatedBuses = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBuses(updatedBuses);
    });

    return () => unsubscribe(); // Limpia la suscripción al desmontar
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddBus = () => {
    if (!newBus.interno || (!newBus.semicama && !newBus.cama)) {
      alert("Por favor, completa todos los campos.");
      return;
    }

    const bus = {
      id: `bus-${buses.length + 1}`,
      interno: newBus.interno,
      semicama: parseInt(newBus.semicama) || 0,
      cama: parseInt(newBus.cama) || 0,
      seats: {},
    };

    setBuses([...buses, bus]);
    setDoc(doc(db, "buses", bus.id), bus); // Save to Firestore
    setNewBus({ interno: "", semicama: "", cama: "" });
    setShowAddBusModal(false);
  };

  const handleSeatClick = (busId, seatId) => {
    const bus = buses.find((b) => b.id === busId);
    const seat = bus.seats[seatId] || {}; // Si no hay datos, usar un objeto vacío

    // Verificar si el asiento ya está ocupado (tiene un voucherNumero)
    if (seat.voucherNumero) {
      alert("Esta butaca ya está ocupada. No se puede modificar.");
      return;
    }

    setSelectedSeat({ busId, seatId, seat });

    setIsSeatOccupied(!!seat.voucherNumero); 

    setFormData({
      voucherNumero: seat.voucherNumero || uuidv4(), // Genera un nuevo voucher si no existe
      nombre: seat.nombre || "",
      apellido: seat.apellido || "",
      dni: seat.dni || "",
      fechaNacimiento: seat.fechaNacimiento || "",
      nacionalidad: seat.nacionalidad || "",
      origen: seat.origen || defaultData.origen,
      destino: seat.destino || defaultData.destino,
      fechaSalida: seat.fechaSalida || defaultData.fechaSalida,
      horaSalida: seat.horaSalida || defaultData.horaSalida,
      fechaRegreso: seat.fechaRegreso || defaultData.fechaRegreso,
      horaRegreso: seat.horaRegreso || defaultData.horaRegreso,
      observaciones: seat.observaciones || defaultData.observaciones,
    });
    setShowModal(true);
  };

  const handleModalSave = async () => {
    const voucherNumero = formData.voucherNumero || uuidv4(); // Genera un número único para el voucher

    // Consulta el estado actual del bus desde Firestore
    const busRef = doc(db, "buses", selectedSeat.busId);
    const busSnapshot = await getDoc(busRef);

    if (busSnapshot.exists()) {
      const currentBusData = busSnapshot.data();
      const currentSeat = currentBusData.seats[selectedSeat.seatId];

      if (currentSeat && currentSeat.voucherNumero) {
        alert("Esta butaca ya está ocupada. Por favor, selecciona otra.");
        setShowModal(false);
        return;
      }

      // Actualiza Firestore con los datos ingresados
      const updatedSeats = {
        ...currentBusData.seats,
        [selectedSeat.seatId]: { ...formData, voucherNumero },
      };

      await updateDoc(busRef, { seats: updatedSeats });

      // Guardar los datos en la colección "vouchers"
      const voucherData = {
        voucherNumero,
        busId: selectedSeat.busId,
        seatId: selectedSeat.seatId,
        ...formData,
      };

      await setDoc(doc(db, "vouchers", voucherNumero), voucherData);

      // Actualiza el estado local
      setBuses((prevBuses) =>
        prevBuses.map((bus) =>
          bus.id === selectedSeat.busId ? { ...bus, seats: updatedSeats } : bus
        )
      );

      // Genera automáticamente el PDF con los datos del asiento actual
      const blob = await pdf(
        <VoucherDocument voucherData={voucherData} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Voucher-${voucherNumero}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url); // Limpia la URL del objeto

      // Enviar email automático (si hay email)
      if (formData.email && formData.email.includes("@")) {
        await enviarVoucherPorEmail(voucherData, blob);
      }

      // Actualiza los datos por defecto para facilitar el ingreso de los siguientes asientos
      setDefaultData({
        origen: formData.origen,
        destino: formData.destino,
        fechaSalida: formData.fechaSalida,
        horaSalida: formData.horaSalida,
        fechaRegreso: formData.fechaRegreso,
        horaRegreso: formData.horaRegreso,
        observaciones: formData.observaciones,
      });

      // Limpia el formulario para el siguiente asiento
      setFormData({
        nombre: "",
        apellido: "",
        dni: "",
        fechaNacimiento: "",
        nacionalidad: "",
        email: "",
        origen: defaultData.origen,
        destino: defaultData.destino,
        fechaSalida: defaultData.fechaSalida,
        horaSalida: defaultData.horaSalida,
        fechaRegreso: defaultData.fechaRegreso,
        horaRegreso: defaultData.horaRegreso,
        observaciones: defaultData.observaciones,
      });

      setShowModal(false);
    }
  };

  const enviarVoucherPorEmail = async (voucherData, pdfBlob) => {
    const fileRef = ref(storage, `vouchers/Voucher-${voucherData.voucherNumero}.pdf`);
    await uploadBytes(fileRef, pdfBlob);
  
    const downloadURL = await getDownloadURL(fileRef);
  
    await addDoc(collection(db, "mail"), {
      to: voucherData.email,
      from: `"CATA Internacional" <${correoUsuario}>`, // 👈 este es el remitente visible
      replyTo: correoUsuario, // 👈 opcional, para respuestas
      message: {
        subject: `Voucher de Viaje - ${voucherData.apellido} ${voucherData.nombre}`,
        html: `<p style="margin: 0px; font-weight: bold;">Estimado/a ${voucherData.nombre},</p>
               <br>
               <p style="margin: 0px;">Adjuntamos su voucher de viaje en el siguiente enlace:</p>
               <p style="margin: 0px;"><a href="${downloadURL}" target="_blank">Descargar Voucher PDF</a></p>
               <br>
               <p style="margin: 0px;">Atentamente,</p>
               <p style="margin: 0px;">${firmaCorreo}</p>
               <p style="margin: 0px;">Gracias por viajar con CATA Internacional.</p>
               <br>
               <hr>
                <p style="margin: 0px; font-size: 12px; color: #555;">
                  Este es un correo automático generado por el sistema de CATA Internacional.
                </p>
               `,
      }
    });
  };

  const handleClear = async () => {
    if (confirmationText.toLowerCase() === "confirmar") {
      setShowGroupNameModal(true); // Abre el modal para solicitar el nombre del grupo
      setShowClearModal(false); // Cierra el modal de confirmación
      setConfirmationText(""); // Limpia el texto de confirmación
    } else {
      alert("Por favor, escribe 'confirmar' correctamente para continuar.");
    }
  };

  const saveGroupToHistory = async () => {
    if (!groupName.trim()) {
      alert("Por favor, ingresa un nombre para el grupo.");
      return;
    }

    try {
      const timestamp = new Date();

      // Guarda el historial en la colección `historial`
      await setDoc(
        doc(db, "historial", `${groupName}-${timestamp.toISOString()}`),
        {
          groupName,
          timestamp,
          buses,
        }
      );

      // Elimina todos los buses de la colección `buses`
      const busesCollection = collection(db, "buses");
      const querySnapshot = await getDocs(busesCollection);

      const deletePromises = querySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises); // Espera a que todos los documentos sean eliminados

      // Limpia el estado local
      setBuses([]);
      setGroupName(""); // Limpia el nombre del grupo
      setShowGroupNameModal(false); // Cierra el modal

      alert("Grupo guardado en el historial y buses eliminados correctamente.");
    } catch (error) {
      console.error("Error al guardar el historial:", error);
      alert("Hubo un error al guardar el historial. Inténtalo nuevamente.");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(
      new Date(dateStr).toLocaleString("en-US", { timeZone: "UTC" })
    );
    if (isNaN(date)) return "";
    return date.toLocaleDateString("es-AR"); // dd/MM/yyyy
  };
  
  const generateExcel = () => {
    const rows = [];
  
    buses.forEach((bus) => {
      Object.entries(bus.seats).forEach(([seatId, seatData]) => {
        rows.push({
          Interno: bus.interno,
          Butaca: seatId,
          Voucher: seatData.voucherNumero || "",
          Apellido: seatData.apellido || "",
          Nombre: seatData.nombre || "",
          Tipo: "",
          DNI: seatData.dni || "",
          Sexo: "",
          Menor: "",
          Nacionalidad: seatData.nacionalidad || "",
          OcupaButaca: "",
          FechaNacimiento: formatDate(seatData.fechaNacimiento),
          Origen: seatData.origen || "",
          Destino: seatData.destino || "",
          FechaSalida: formatDate(seatData.fechaSalida),
          HoraSalida: seatData.horaSalida || "",
          FechaRegreso: formatDate(seatData.fechaRegreso),
          HoraRegreso: seatData.horaRegreso || "",
          Observaciones: seatData.observaciones || "",
        });
      });
    });
  
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Buses");
  
    XLSX.writeFile(workbook, "Buses_Planilla.xlsx");
  };

  return (
    <Container>
      <Button onClick={() => setShowAddBusModal(true)}>Añadir Bus</Button>
      <Button onClick={() => setShowClearModal(true)}>Limpiar Todo</Button>
      <Button onClick={generateExcel}>Generar Planilla</Button>

      <BusContainer>
        {buses.map((bus) => (
          <Bus key={bus.id}>
            <Title>Interno {bus.interno}</Title>
            {bus.semicama > 0 && (
              <Deck>
                {[...Array(Math.ceil(bus.semicama / 4))].map((_, rowIndex) => (
                  <div
                    key={`semicama-row-${rowIndex}`}
                    style={{ display: "flex", gap: "5px", marginBottom: "5px" }}
                  >
                    {/* Butacas 1 y 2 (Izquierda) */}
                    {rowIndex * 4 + 1 <= bus.semicama && (
                      <Seat
                        key={`semicama-${rowIndex * 4}`}
                        color={
                          bus.seats[`semicama-${rowIndex * 4}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(bus.id, `semicama-${rowIndex * 4}`)
                        }
                      >
                        {rowIndex * 4 + 1}
                      </Seat>
                    )}
                    {rowIndex * 4 + 2 <= bus.semicama && (
                      <Seat
                        key={`semicama-${rowIndex * 4 + 1}`}
                        color={
                          bus.seats[`semicama-${rowIndex * 4 + 1}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `semicama-${rowIndex * 4 + 1}`
                          )
                        }
                      >
                        {rowIndex * 4 + 2}
                      </Seat>
                    )}

                    {/* Espacio central */}
                    <div
                      style={{ width: "8px", backgroundColor: "grey" }}
                    ></div>

                    {/* Butacas 3 y 4 (Derecha) */}
                    {rowIndex * 4 + 3 <= bus.semicama && (
                      <Seat
                        key={`semicama-${rowIndex * 4 + 2}`}
                        color={
                          bus.seats[`semicama-${rowIndex * 4 + 2}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `semicama-${rowIndex * 4 + 2}`
                          )
                        }
                      >
                        {rowIndex * 4 + 3}
                      </Seat>
                    )}
                    {rowIndex * 4 + 4 <= bus.semicama && (
                      <Seat
                        key={`semicama-${rowIndex * 4 + 3}`}
                        color={
                          bus.seats[`semicama-${rowIndex * 4 + 3}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `semicama-${rowIndex * 4 + 3}`
                          )
                        }
                      >
                        {rowIndex * 4 + 4}
                      </Seat>
                    )}
                  </div>
                ))}
              </Deck>
            )}
            {bus.cama > 0 && (
              <Deck>
                {[...Array(Math.ceil(bus.cama / 3))].map((_, rowIndex) => (
                  <div
                    key={`cama-row-${rowIndex}`}
                    style={{
                      display: "flex",
                      gap: "5px",
                      marginBottom: "5px",
                      justifyContent: "center",
                    }}
                  >
                    {/* Butaca 1 (Lado Izquierdo) */}
                    {rowIndex * 3 + 1 <= bus.cama && (
                      <Seat
                        key={`cama-${rowIndex * 3}`}
                        color={
                          bus.seats[`cama-${rowIndex * 3 + bus.semicama}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `cama-${rowIndex * 3 + bus.semicama}`
                          )
                        }
                      >
                        {rowIndex * 3 + 1 + bus.semicama}
                      </Seat>
                    )}

                    {/* Butaca 2 (Lado Izquierdo) */}
                    {rowIndex * 3 + 2 <= bus.cama && (
                      <Seat
                        key={`cama-${rowIndex * 3 + 1}`}
                        color={
                          bus.seats[`cama-${rowIndex * 3 + 1 + bus.semicama}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `cama-${rowIndex * 3 + 1 + bus.semicama}`
                          )
                        }
                      >
                        {rowIndex * 3 + 2 + bus.semicama}
                      </Seat>
                    )}

                    {/* Espacio Vacío */}
                    <div
                      style={{ width: "44px", backgroundColor: "gray" }}
                    ></div>

                    {/* Butaca 3 (Lado Derecho) */}
                    {rowIndex * 3 + 3 <= bus.cama && (
                      <Seat
                        key={`cama-${rowIndex * 3 + 2}`}
                        color={
                          bus.seats[`cama-${rowIndex * 3 + 2 + bus.semicama}`]
                            ? "#c20000"
                            : null
                        }
                        onClick={() =>
                          handleSeatClick(
                            bus.id,
                            `cama-${rowIndex * 3 + 2 + bus.semicama}`
                          )
                        }
                      >
                        {rowIndex * 3 + 3 + bus.semicama}
                      </Seat>
                    )}
                  </div>
                ))}
              </Deck>
            )}
          </Bus>
        ))}
      </BusContainer>

      {/* Add Bus Modal */}
      {showAddBusModal && (
        <>
          <Overlay onClick={() => setShowAddBusModal(false)} />
          <Modal>
            <h3>Añadir Bus</h3>
            <FormContainer>
              <input
                type="text"
                placeholder="Número de Interno"
                value={newBus.interno || ""}
                onChange={(e) =>
                  setNewBus({ ...newBus, interno: e.target.value })
                }
              />
              <input
                type="number"
                placeholder="Cantidad de Butacas Semicama"
                value={newBus.semicama}
                onChange={(e) =>
                  setNewBus({ ...newBus, semicama: e.target.value })
                }
              />
              <input
                type="number"
                placeholder="Cantidad de Butacas Cama"
                value={newBus.cama}
                onChange={(e) => setNewBus({ ...newBus, cama: e.target.value })}
              />
            </FormContainer>
            <Button onClick={handleAddBus}>Guardar</Button>
          </Modal>
        </>
      )}

      {/* Confirmación de Limpieza */}
      {showClearModal && (
        <>
          <Overlay onClick={() => setShowClearModal(false)} />
          <Modal>
            <h3>Confirmar Limpieza</h3>
            <p>
              Esta acción eliminará los buses actuales y los guardará en el
              historial.
            </p>
            <p>
              Para confirmar, escribe <strong>'confirmar'</strong>:
            </p>
            <input
              type="text"
              placeholder="Escribe 'confirmar'"
              value={confirmationText || ""}
              onChange={(e) => setConfirmationText(e.target.value)}
            />
            <Button onClick={handleClear}>Confirmar</Button>
            <Button onClick={() => setShowClearModal(false)}>Cancelar</Button>
          </Modal>
        </>
      )}

      {/* Modal para Nombre del Grupo */}
      {showGroupNameModal && (
        <>
          <Overlay onClick={() => setShowGroupNameModal(false)} />
          <Modal>
            <h3>Guardar Grupo</h3>
            <p>Ingresa un nombre para este grupo de buses:</p>
            <input
              type="text"
              placeholder="Nombre del grupo"
              value={groupName || ""}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <Button onClick={saveGroupToHistory}>Guardar</Button>
            <Button onClick={() => setShowGroupNameModal(false)}>
              Cancelar
            </Button>
          </Modal>
        </>
      )}

      {/* Seat Modal */}
      {showModal && (
        <>
          <Overlay onClick={() => setShowModal(false)} />
          <Modal>
            <h3>Datos del Pasajero</h3>
            <FormContainer>
              <input
                type="text"
                placeholder="Nombre"
                value={formData.nombre || ""}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="Apellido"
                value={formData.apellido || ""}
                onChange={(e) =>
                  setFormData({ ...formData, apellido: e.target.value })
                }
              />
              <input
                type="number"
                placeholder="DNI"
                value={formData.dni}
                onChange={(e) =>
                  setFormData({ ...formData, dni: e.target.value })
                }
              />
              <FormGroup>
                <label>Fecha de Nac.</label>
                <input
                  type="date"
                  placeholder="Fecha de Nacimiento"
                  value={formData.fechaNacimiento}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      fechaNacimiento: e.target.value,
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Nacionalidad</label>
                <input
                  type="text"
                  placeholder="Nacionalidad"
                  value={formData.nacionalidad || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      nacionalidad: e.target.value,
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      email: e.target.value,
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Desde</label>
                <input
                  type="text"
                  placeholder="Origen"
                  value={formData.origen || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, origen: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Hasta</label>
                <input
                  type="text"
                  placeholder="Destino"
                  value={formData.destino || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, destino: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Fecha de Salida</label>
                <input
                  type="date"
                  placeholder="Fecha de Salida"
                  value={formData.fechaSalida}
                  onChange={(e) =>
                    setFormData({ ...formData, fechaSalida: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Hora de Salida</label>
                <input
                  type="time"
                  placeholder="Hora de Salida"
                  value={formData.horaSalida}
                  onChange={(e) =>
                    setFormData({ ...formData, horaSalida: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Fecha de Regreso</label>
                <input
                  type="date"
                  placeholder="Fecha de Regreso"
                  value={formData.fechaRegreso}
                  onChange={(e) =>
                    setFormData({ ...formData, fechaRegreso: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Hora de Regreso</label>
                <input
                  type="time"
                  placeholder="Hora de Regreso"
                  value={formData.horaRegreso}
                  onChange={(e) =>
                    setFormData({ ...formData, horaRegreso: e.target.value })
                  }
                />
              </FormGroup>
              <textarea
                placeholder="Observaciones"
                value={formData.observaciones}
                onChange={(e) =>
                  setFormData({ ...formData, observaciones: e.target.value })
                }
                rows="3"
              />
            </FormContainer>
            <Button disabled={isSeatOccupied} onClick={handleModalSave} >Guardar</Button>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default BusDiagrama;
